import React, { useState } from 'react'
import { Button, Flex, Input, Typography } from 'antd'
import { useUpdateQuestionAnswerEscalatedMutation } from '../../../../../../store/apis/submission.api'
import { useParams } from 'react-router'
import { useVersionId } from '../../../../../../utils/parseURL.helper'
import useQueryParam from '../../../../../../hooks/useQueryParam'
import { SUBMISSION_SEARCH_PARAM } from '../../../../../../constants/query-params'
import { ExplainabilityStatuses } from '../../../../../../constants/explainability-statuses'
import { useAutoFocusFirstInput } from '../../../../../../../hooks/useAutoFocusFirstInput'

const { TextArea } = Input
const { Text } = Typography

const FlagNotePopoverContent = ({ 
    answerId,
    questionRubricId,
    submissionId, 
    onSaveNote, 
    onCancel,
    onClearFlag,
    onDeleteNote,
    onReFlag,
    existingNote = '',
    isNewFlag = false,
    isFlagged = false,
    isClearing = false,
}) => {
    const [note, setNote] = useState(existingNote || '')
    const { token } = useParams()
    const versionId = useVersionId()
    const search = useQueryParam(SUBMISSION_SEARCH_PARAM)
    useAutoFocusFirstInput()
    
    const [updateQuestionAnswerEscalated, { isLoading: isSaving }] = useUpdateQuestionAnswerEscalatedMutation()

    const hasChanges = note.trim() !== (existingNote || '').trim()
    const hasExistingNote = Boolean(existingNote?.trim())

    const handleSaveNote = async () => {
        if (note.trim() || hasExistingNote) {
            await updateQuestionAnswerEscalated({ 
                id: answerId, 
                questionRubricId,
                submissionId,
                escalated: ExplainabilityStatuses.MANUAL_UNRESOLVED,
                note: note.trim(),
                search,
                versionId,
                token,
            })
        }
        onSaveNote()
    }

    const handleSkipOrClose = () => {
        onCancel()
    }

    // State 1: Viewing/editing an existing flag (flagged = true)
    if (isFlagged && !isNewFlag) {
        return (
            <Flex vertical gap={8}>
                <Text type="secondary">
                    {hasExistingNote ? 'Edit flag note' : 'Add a note to this flag'}
                </Text>
                <TextArea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Add a note about this flag..."
                    autoSize={{ minRows: 2, maxRows: 4 }}
                    style={{ minWidth: 280 }}
                    data-test='note-input'
                />
                <Flex justify="space-between" align="center" gap={8}>
                    <Button 
                        size="small" 
                        onClick={onClearFlag}
                        loading={isClearing}
                        disabled={isSaving}
                        danger
                        type="text"
                        data-test='clear-flag-button'
                    >
                        Clear flag
                    </Button>
                    <Flex gap={8}>
                        <Button 
                            size="small" 
                            onClick={handleSkipOrClose}
                            disabled={isSaving || isClearing}
                            data-test='cancel-note-button'
                        >
                            Cancel
                        </Button>
                        <Button 
                            size="small"
                            type="primary"
                            onClick={handleSaveNote}
                            loading={isSaving}
                            disabled={!hasChanges || isClearing}
                            data-test='save-note-button'
                        >
                            Save
                        </Button>
                    </Flex>
                </Flex>
            </Flex>
        )
    }

    // State 2: Viewing unflagged with note (flagged = false, has note)
    if (!isFlagged && !isNewFlag && hasExistingNote) {
        return (
            <Flex vertical gap={8}>
                <Text type="secondary">
                    Flag was cleared
                </Text>
                <TextArea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Note content..."
                    autoSize={{ minRows: 2, maxRows: 4 }}
                    style={{ minWidth: 280 }}
                />
                <Flex justify="space-between" align="center" gap={8}>
                    <Button 
                        size="small" 
                        onClick={onDeleteNote}
                        loading={isClearing}
                        disabled={isSaving}
                        danger
                        type="text"
                    >
                        Delete note
                    </Button>
                    <Flex gap={8}>
                        {hasChanges && (
                            <Button 
                                size="small"
                                onClick={handleSaveNote}
                                loading={isSaving}
                                disabled={isClearing}
                            >
                                Save
                            </Button>
                        )}
                        <Button 
                            size="small"
                            type="primary"
                            onClick={onReFlag}
                            loading={isSaving}
                            disabled={isClearing}
                        >
                            Re-flag
                        </Button>
                    </Flex>
                </Flex>
            </Flex>
        )
    }

    // State 3: Just flagged - prompting to add a note
    const hasNoteText = note.trim().length > 0
    
    return (
        <Flex vertical gap={8}>
            <Text type="secondary">
                Add a note to this flag (optional)
            </Text>
            <TextArea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Why are you flagging this?"
                data-test="note-input"
                autoSize={{ minRows: 2, maxRows: 4 }}
                style={{ minWidth: 280 }}
            />
            <Flex justify="flex-end" align="center" gap={8}>
                {hasNoteText ? (
                    <>
                        <Button 
                            size="small" 
                            onClick={handleSkipOrClose}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <Button 
                            size="small"
                            type="primary"
                            onClick={handleSaveNote}
                            loading={isSaving}
                        >
                            Add note
                        </Button>
                    </>
                ) : (
                    <Button 
                        size="small"
                        type="primary"
                        onClick={handleSkipOrClose}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                )}
            </Flex>
        </Flex>
    )
}

export default FlagNotePopoverContent